/**
 * Media moved to NMP (assets.sp.nike.com). NMP only serves video at the plain
 * upload path: any Cloudinary-style transform on a video URL returns a 404.
 * Images with transforms keep working, so only video URLs are rewritten here.
 */

const VIDEO_UPLOAD_SEGMENT = '/video/upload/';
const VERSION_SEGMENT = /^v\d+$/;
// A single Cloudinary transform param, e.g. `c_fill`, `w_1080`, `q_80`, `$var_x`.
const TRANSFORM_PARAM = /^\$?[a-z]{1,3}_[^/]*$/i;
const IMAGE_EXTENSION = /\.(jpe?g|png|webp|gif|avif|svg|bmp|tiff?)$/i;
const M3U8_EXTENSION = /\.m3u8$/i;
const VIDEO_RESOURCE_TYPES = ['video', 'm3u8-video'];
// Fields that are never URLs to rewrite, or that intentionally point at images.
const SKIPPED_KEYS = /^(etag)$|thumb|poster/i;

const isTransformSegment = (segment: string): boolean =>
	segment.length > 0 && segment.split(',').every((part) => TRANSFORM_PARAM.test(part));

const splitUrl = (url: string): { path: string; query: string; hash: string } => {
	const hashIndex = url.indexOf('#');
	const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
	const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
	const queryIndex = withoutHash.indexOf('?');
	const query = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '';
	const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;

	return { path, query, hash };
};

const getPath = (url: string): string => splitUrl(url).path;

const NMP_HOST = 'assets.sp.nike.com';

/** True for URLs served by NMP, which has no derived formats (e.g. no `.webm` of an `.mp4`). */
export const isNmpUrl = (value: unknown): value is string =>
	typeof value === 'string' && value.includes(NMP_HOST);

/** True for any string whose path contains `/video/upload/` (incl. poster frames). */
export const isVideoUploadUrl = (value: unknown): value is string =>
	typeof value === 'string' && getPath(value).includes(VIDEO_UPLOAD_SEGMENT);

/**
 * A string that points at a video asset: a `/video/upload/` URL that isn't an
 * image (Cloudinary can serve poster frames as `.jpg` from the video path).
 */
export const isVideoUrl = (value: unknown): value is string =>
	isVideoUploadUrl(value) && !IMAGE_EXTENSION.test(getPath(value));

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
	if (value === null || typeof value !== 'object') {
		return false;
	}
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
};

/**
 * Removes Cloudinary transform segments (and the `_a` analytics param) from a
 * video URL. Non-video URLs, and non-strings, are returned unchanged.
 *
 * `.../video/upload/c_fill,w_1080/e_sharpen/q_80/v123/folder/file.mp4?_a=X`
 *   -> `.../video/upload/v123/folder/file.mp4`
 */
export function stripVideoTransforms<T>(url: T): T {
	if (!isVideoUploadUrl(url)) {
		return url;
	}

	const { path, query, hash } = splitUrl(url);
	const uploadIndex = path.indexOf(VIDEO_UPLOAD_SEGMENT) + VIDEO_UPLOAD_SEGMENT.length;
	const prefix = path.slice(0, uploadIndex);
	let segments = path.slice(uploadIndex).split('/');

	const versionIndex = segments.findIndex((segment) => VERSION_SEGMENT.test(segment));
	if (versionIndex >= 0) {
		segments = segments.slice(versionIndex);
	} else {
		// No version: drop leading transform-looking segments, but never the file itself.
		while (segments.length > 1 && isTransformSegment(segments[0])) {
			segments.shift();
		}
	}

	const keptParams = query
		.split('&')
		.filter((param) => param !== '' && param !== '_a' && !param.startsWith('_a='));
	const queryString = keptParams.length ? `?${keptParams.join('&')}` : '';

	return `${prefix}${segments.join('/')}${queryString}${hash}` as unknown as T;
}

/**
 * Guard for code that adds Cloudinary transforms: true when the media (or URL)
 * is a video. An explicit `resource_type`/`subType` decides (`video` or
 * `m3u8-video` => true, anything else, e.g. `image` => false). Untyped media and
 * plain strings count as video when the URL is under `/video/upload/` and isn't
 * an image file. Never transform these: NMP returns 404 for transformed videos.
 */
export const isVideoAsset = (media: unknown): boolean => {
	if (typeof media === 'string') {
		return isVideoUrl(media);
	}
	if (!media || typeof media !== 'object') {
		return false;
	}
	const { resource_type, subType, url, secure_url } = media as Record<string, unknown>;
	const type = resource_type ?? subType;
	if (typeof type === 'string') {
		// An explicit type wins, so typed images are never treated as video.
		return VIDEO_RESOURCE_TYPES.includes(type);
	}
	return isVideoUrl(url) || isVideoUrl(secure_url);
};

const isVideoMedia = (node: Record<string, unknown>): boolean => {
	const resourceType = node.resource_type ?? node.subType;
	if (typeof resourceType === 'string') {
		return VIDEO_RESOURCE_TYPES.includes(resourceType);
	}
	return isVideoUrl(node.url);
};

const normalizeVideoMedia = (node: Record<string, unknown>): void => {
	const isM3u8 = (node.resource_type ?? node.subType) === 'm3u8-video';
	const original = typeof node.original_url === 'string' && node.original_url !== ''
		? stripVideoTransforms(node.original_url)
		: null;

	if (original && (!isM3u8 || M3U8_EXTENSION.test(getPath(original)))) {
		node.url = original;
	} else if (typeof node.url === 'string') {
		node.url = stripVideoTransforms(node.url);
	}
};

/**
 * Deeply walks `data` (arrays and plain objects only; Dates, class instances,
 * Blobs, etc. are left alone) and rewrites video URLs so they load from NMP:
 *  - video media objects (`resource_type`/`subType` is `video`/`m3u8-video`, or
 *    `url` is a `/video/upload/` URL) get `url` = `original_url`, falling back
 *    to `stripVideoTransforms(url)`. For `m3u8-video`, `original_url` is only
 *    used when it is an m3u8 itself.
 *  - any other string value that is a video URL (e.g. `secure_url`) is passed
 *    through `stripVideoTransforms`.
 * `etag`, thumbnail/poster fields and image URLs are never touched.
 *
 * Mutates `data` IN PLACE and returns the same reference, so it is cheap on
 * large channel payloads and callers can write `x = normalizeVideoUrls(x)`.
 * It is idempotent, so normalizing the same data twice is harmless.
 */
export function normalizeVideoUrls<T>(data: T): T {
	const seen = new WeakSet<object>();

	const walk = (node: unknown): void => {
		if (Array.isArray(node)) {
			if (seen.has(node)) return;
			seen.add(node);
			node.forEach((item, index) => {
				if (isVideoUrl(item)) {
					node[index] = stripVideoTransforms(item);
				} else {
					walk(item);
				}
			});
			return;
		}

		if (!isPlainObject(node) || seen.has(node)) {
			return;
		}
		seen.add(node);

		if (isVideoMedia(node)) {
			normalizeVideoMedia(node);
		}

		Object.keys(node).forEach((key) => {
			const value = node[key];
			if (typeof value === 'string') {
				if (key !== 'url' && !SKIPPED_KEYS.test(key) && isVideoUrl(value)) {
					node[key] = stripVideoTransforms(value);
				}
				return;
			}
			walk(value);
		});
	};

	walk(data);
	return data;
}

export default normalizeVideoUrls;
